import {
  BarChart3,
  ClipboardList,
  Wrench,
  Waves,
  Megaphone,
  CalendarDays,
  FolderOpen,
  Scale,
  FileText,
  FileSignature,
  Receipt,
  Gavel,
  ShieldCheck,
  Users,
  Home,
  Folder,
} from "lucide-react";
import { apiFetch, getApiUrl } from "../auth/apiClient";

// Maps a category's `icon` name (stored in the DB, shared with mobile)
// to a lucide component. Anything unknown falls back to a folder.
export const CATEGORY_ICONS = {
  BarChart3,
  ClipboardList,
  Wrench,
  Waves,
  Megaphone,
  CalendarDays,
  FolderOpen,
  Scale,
  FileText,
  FileSignature,
  Receipt,
  Gavel,
  ShieldCheck,
  Users,
  Home,
  Folder,
};

// Palette offered to admins when creating a category.
export const CATEGORY_COLORS = [
  "#1B5E20", "#1565C0", "#6D28D9", "#0F766E", "#D97706", // brand-color-ok: data palette, not a theme token
  "#0284C7", "#C62828", "#2E7D32", "#9333EA", "#475569",
];

export function iconFor(name) {
  return CATEGORY_ICONS[name] || FolderOpen;
}

// Static fallback metadata for the built-in keys — used only until the
// live category list (which carries color/icon/label) has loaded, and as
// a backstop for any key not present in the cache.
export const CATEGORY_META = {
  FINANCE:       { label: "Finance",       Icon: BarChart3,     color: "#1B5E20", bg: "#DCFCE7" }, // brand-color-ok: static category fallback, overridden by live API data
  GOVERNANCE:    { label: "Governance",    Icon: ClipboardList, color: "#1565C0", bg: "#DBEAFE" },
  LEGAL_DOCS:    { label: "Legal Docs",    Icon: Scale,         color: "#6D28D9", bg: "#EDE9FE" },
  FORMS:         { label: "Forms",         Icon: FileText,      color: "#0F766E", bg: "#CCFBF1" },
  MAINTENANCE:   { label: "Maintenance",   Icon: Wrench,        color: "#D97706", bg: "#FEF3C7" },
  AMENITIES:     { label: "Amenities",     Icon: Waves,         color: "#0284C7", bg: "#E0F2FE" },
  ANNOUNCEMENTS: { label: "Announcements", Icon: Megaphone,     color: "#C62828", bg: "#FEE2E2" },
  GBM:           { label: "GBM",           Icon: CalendarDays,  color: "#2E7D32", bg: "#D1FAE5" },
  OTHER:         { label: "Other",         Icon: FolderOpen,    color: "#475569", bg: "#F1F5F9" },
};

// Module-level cache of the live categories (key -> {label,color,icon}),
// populated whenever listCategories() runs. Lets leaf components resolve a
// category key to its label/color/icon without prop-drilling.
const _catCache = new Map();

function _hexToBg(hex) {
  // Light translucent tint of the category color for icon chips.
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return "#F1F5F9";
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h, 16));
  return `rgba(${r}, ${g}, ${b}, 0.12)`;
}

// Resolve a category key to display metadata, preferring the live cache.
export function categoryMeta(key) {
  const cached = _catCache.get(key);
  if (cached) {
    return {
      label: cached.label,
      color: cached.color,
      Icon: iconFor(cached.icon),
      bg: _hexToBg(cached.color),
    };
  }
  if (CATEGORY_META[key]) return CATEGORY_META[key];
  // Unknown (e.g. a deleted custom category still stamped on a doc).
  const label = String(key || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { label: label || "Other", Icon: FolderOpen, color: "#475569", bg: "#F1F5F9" };
}

export const docsApi = {
  listCategories: async () => {
    const data = await apiFetch("/api/documents/categories", {
      authRequired: true,
    });
    // Warm the key -> meta cache for leaf components.
    (data.categories || []).forEach((c) => {
      if (c.key) {
        _catCache.set(c.key, {
          label: c.label,
          color: c.color,
          icon: c.icon,
        });
      }
    });
    return data;
  },
  createCategory: (payload) =>
    apiFetch("/api/documents/categories", {
      method: "POST",
      authRequired: true,
      body: payload,
    }),
  updateCategory: (id, payload) =>
    apiFetch(`/api/documents/categories/${id}`, {
      method: "PATCH",
      authRequired: true,
      body: payload,
    }),
  deleteCategory: (id) =>
    apiFetch(`/api/documents/categories/${id}`, {
      method: "DELETE",
      authRequired: true,
    }),
  list: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch(`/api/documents${suffix}`, { authRequired: true });
  },
  pendingCount: () =>
    apiFetch("/api/documents/pending-count", { authRequired: true }),
  upload: ({ file, title, description, category, tags, visibility, bundle_id, request_internet_open }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("description", description || "");
    fd.append("category", category);
    fd.append("tags", JSON.stringify(tags || []));
    fd.append("visibility", visibility);
    if (bundle_id) fd.append("bundle_id", bundle_id);
    if (request_internet_open) fd.append("request_internet_open", "true");
    return apiFetch("/api/documents", {
      method: "POST",
      authRequired: true,
      formData: fd,
    });
  },
  // Upload several files into one (optionally new) bundle. Each file
  // becomes its own document titled by its filename. Returns the list of
  // {file, ok, error} results so the caller can report partial failures.
  uploadMany: async ({ files, category, visibility, bundle_id, tags, request_internet_open, onProgress }) => {
    const results = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const title = (file.name || "Document").replace(/\.[^.]+$/, "");
      try {
        await docsApi.upload({
          file,
          title,
          description: "",
          category,
          tags: tags || [],
          visibility,
          bundle_id,
          request_internet_open,
        });
        results.push({ file, ok: true });
      } catch (e) {
        results.push({ file, ok: false, error: e.message || "Upload failed" });
      }
      if (onProgress) onProgress(i + 1, files.length);
    }
    return results;
  },
  // ----- Bundles -----
  listBundles: (category) => {
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    return apiFetch(`/api/documents/bundles${qs}`, { authRequired: true });
  },
  createBundle: (payload) =>
    apiFetch("/api/documents/bundles", {
      method: "POST",
      authRequired: true,
      body: payload,
    }),
  getBundle: (id) =>
    apiFetch(`/api/documents/bundles/${id}`, { authRequired: true }),
  patchBundle: (id, payload) =>
    apiFetch(`/api/documents/bundles/${id}`, {
      method: "PATCH",
      authRequired: true,
      body: payload,
    }),
  deleteBundle: (id) =>
    apiFetch(`/api/documents/bundles/${id}`, {
      method: "DELETE",
      authRequired: true,
    }),
  assignDocToBundle: (docId, bundleId) =>
    apiFetch(`/api/documents/${docId}/bundle`, {
      method: "PATCH",
      authRequired: true,
      body: { bundle_id: bundleId },
    }),
  approve: (id) =>
    apiFetch(`/api/documents/${id}/approve`, {
      method: "POST",
      authRequired: true,
    }),
  reject: (id, reason, feedback) =>
    apiFetch(`/api/documents/${id}/reject`, {
      method: "POST",
      authRequired: true,
      body: { reason, feedback },
    }),
  delete: (id) =>
    apiFetch(`/api/documents/${id}`, {
      method: "DELETE",
      authRequired: true,
    }),
  downloadUrl: (id) => `${getApiUrl()}/api/documents/${id}/download`,
  previewUrl: (id) => `${getApiUrl()}/api/documents/${id}/preview`,
  setInternetOpen: (id, isOpen) =>
    apiFetch(`/api/documents/${id}/internet-open`, {
      method: "PATCH",
      authRequired: true,
      body: { is_internet_open: !!isOpen },
    }),
  listDownloads: (id) =>
    apiFetch(`/api/documents/${id}/downloads`, { authRequired: true }),
  // ---- Public (anonymous) ----
  publicList: ({ category, search, limit = 12, offset = 0 } = {}) => {
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (search) qs.set("search", search);
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));
    return apiFetch(`/api/public/documents?${qs.toString()}`);
  },
  publicCategories: () => apiFetch(`/api/public/documents/categories`),
  publicDownloadUrl: (id) =>
    `${getApiUrl()}/api/public/documents/${id}/download`,
  registerView: (id) =>
    apiFetch(`/api/documents/${id}/view`, {
      method: "POST",
      authRequired: true,
    }),
  listComments: (id) =>
    apiFetch(`/api/documents/${id}/comments`, { authRequired: true }),
  addComment: (id, body) =>
    apiFetch(`/api/documents/${id}/comments`, {
      method: "POST",
      authRequired: true,
      body: { body },
    }),
  deleteComment: (id, commentId) =>
    apiFetch(`/api/documents/${id}/comments/${commentId}`, {
      method: "DELETE",
      authRequired: true,
    }),
};

export async function fetchBlobWithAuth(url) {
  const token = localStorage.getItem("access_token");
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Failed: ${res.status}`);
  return await res.blob();
}

export function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
